import { create } from 'zustand';
import { api, apiUrl } from './api';

// Updating the Android app from inside it. App 1.5.0+ downloads and installs updates itself
// (VBNative.installUpdate). Older apps ignore downloads in their WebView, so for them the link is
// copied to paste into Chrome once; from 1.5.0 on, updates happen inside the app.

export interface AndroidRelease {
  version: string;
  versionCode: number;
  size: number;
  file: string;
  minAndroid: string;
}

const bridge = () => (typeof window !== 'undefined' ? window.VBNative : undefined);

export const useAppUpdate = create<{ latest: AndroidRelease | null; state: string }>(() => ({ latest: null, state: '' }));

/** The installed app's version code (older apps only report the name, e.g. "1.3.0"). */
function installedCode(): number {
  const b = bridge();
  if (!b) return 0;
  if (b.versionCode) return b.versionCode();
  const [maj = 0, min = 0, pat = 0] = (b.version() || '0').split('.').map(Number);
  return maj * 10000 + min * 100 + pat; // compared only against names below
}

function codeOfName(v: string) {
  const [maj = 0, min = 0, pat = 0] = v.split('.').map(Number);
  return maj * 10000 + min * 100 + pat;
}

/** Is there a newer Android app than the one this page runs in? */
export function updateAvailable(latest: AndroidRelease | null): boolean {
  const b = bridge();
  if (!b || !latest) return false;
  return b.versionCode ? latest.versionCode > b.versionCode() : codeOfName(latest.version) > installedCode();
}

export async function checkAppUpdate() {
  if (!bridge()) return;
  const r = await api.apps().catch(() => null);
  useAppUpdate.setState({ latest: (r?.android as AndroidRelease | undefined) ?? null });
}

/** Can this app install updates by itself (1.5.0+)? Older apps need Chrome once. */
export const canSelfUpdate = () => !!bridge()?.installUpdate;

/** Update now: in app 1.5.0+ the app downloads and installs; older apps get the link copied for Chrome. */
export async function startAppUpdate(file: string): Promise<'started' | 'copied' | 'failed'> {
  const url = apiUrl(`/download/${file}`);
  const b = bridge();
  if (b?.installUpdate) {
    b.installUpdate(url);
    return 'started';
  }
  // Apps before 1.5.0 ignore downloads; Chrome handles them. (A trailing-dot address to make the
  // old app open Chrome by itself was tried: Go's TLS rejects such server names, so Chrome showed
  // "can't provide a secure connection".)
  return navigator.clipboard.writeText(url).then(
    () => 'copied' as const,
    () => 'failed' as const,
  );
}

if (typeof window !== 'undefined') {
  window.addEventListener('vbupdate', (e) => useAppUpdate.setState({ state: (e as CustomEvent<{ state: string }>).detail.state }));
}
